/**
 * SSE subscription with resumption and backoff.
 *
 * EventSource cannot set an Authorization header, so the stream is authorized
 * with a short-lived token scoped to one session rather than by putting a
 * session JWT in the URL.
 *
 * Resumption matters more here than it looks: `after_id` is what stops a
 * reconnect from replaying the whole session's transcript into the panel, and
 * it is why the last seen event id is tracked in a ref rather than state — it
 * must survive a reconnect without re-running this effect.
 */
import { useEffect, useRef } from 'react';

import { getStreamToken } from '@/api/client';
import type { ServerEvent } from '@/api/types';

const EVENT_KINDS = [
  'session.created',
  'narration.posted',
  'transcript.appended',
  'checkpoint.created',
  'checkpoint.rejected',
  'frame.ready',
  'frame.failed',
  'session.suspended',
  'canon.updated',
  'chat.message',
  'encounter.ready',
  'encounter.pitched',
  // What the agent is doing between completions. Everything else on this
  // list announces that something finished.
  'session.thinking',
] as const;

const INITIAL_RETRY_MS = 1_200;
const MAX_RETRY_MS = 30_000;

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'retrying';

interface Options {
  sessionId: string | null;
  onEvent: (event: ServerEvent) => void;
  onReconnect?: () => void;
  onStateChange?: (state: ConnectionState) => void;
}

export function useEventStream({
  sessionId,
  onEvent,
  onReconnect,
  onStateChange,
}: Options & { onStateChange?: (s: ConnectionState) => void }): void {
  // Keep callbacks in refs so a caller passing inline functions doesn't tear
  // down and rebuild the stream on every render.
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  const onStateChangeRef = useRef(onStateChange);
  onEventRef.current = onEvent;
  onReconnectRef.current = onReconnect;
  onStateChangeRef.current = onStateChange;

  const lastEventIdRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    let source: EventSource | null = null;
    let retryTimer: number | undefined;
    let retryDelay = INITIAL_RETRY_MS;
    let hadConnection = false;

    const setState = (s: ConnectionState) => onStateChangeRef.current?.(s);

    async function connect(): Promise<void> {
      if (cancelled) return;
      setState('connecting');
      try {
        const { token } = await getStreamToken(sessionId!);
        if (cancelled) return;

        const url =
          `/api/sessions/${encodeURIComponent(sessionId!)}/events` +
          `?after_id=${lastEventIdRef.current}` +
          `&stream_token=${encodeURIComponent(token)}`;
        source = new EventSource(url);

        source.onopen = () => {
          if (cancelled) return;
          retryDelay = INITIAL_RETRY_MS;
          setState('open');
          // Anything that happened while disconnected and is not replayed by
          // after_id (checkpoint bodies, chat history) is refetched here.
          if (hadConnection) onReconnectRef.current?.();
          hadConnection = true;
        };

        for (const kind of EVENT_KINDS) {
          source.addEventListener(kind, (ev) => {
            const message = ev as MessageEvent<string>;
            const id = Number.parseInt(message.lastEventId || '', 10);
            if (Number.isFinite(id)) lastEventIdRef.current = id;
            let data: unknown = {};
            try {
              data = JSON.parse(message.data) as unknown;
            } catch {
              /* a malformed frame must not kill the stream */
            }
            onEventRef.current({ kind, data } as ServerEvent);
          });
        }

        source.onerror = () => {
          if (cancelled) return;
          scheduleRetry();
        };
      } catch {
        if (!cancelled) scheduleRetry();
      }
    }

    function scheduleRetry(): void {
      source?.close();
      source = null;
      if (cancelled || retryTimer !== undefined) return;
      setState('retrying');
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        // Exponential with a ceiling, so a long outage doesn't turn into a
        // tight reconnect loop against the edge.
        retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
        void connect();
      }, retryDelay);
    }

    void connect();

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      source?.close();
      setState('idle');
    };
  }, [sessionId]);
}
